import React, { useContext, useEffect, useMemo, useRef } from "react";
import classNames from "classnames";
import { getPeriodKey, isNLevelClassReport } from "@common/helpers/reports";
import { ViewTable, ViewTableHandle } from "@design-system/ViewTable/ViewTable";
import { AccountingReportContext } from "../AccountingReport.context";
import { GlTableCallbacks, GlTableMeta } from "../glDrillDown.lib";
import { hasSingleValueColumn } from "../singleColumnReport";
import { buildBalanceSheetRows } from "./balanceSheetViewBuilder";
import {
    buildBalanceSheetConfig,
    buildBalanceSheetView,
} from "./balanceSheetViewConfig";
import { BalanceSheetReport } from "./types";
import styles from "./BalanceSheetTable.module.scss";

interface Props {
    report: BalanceSheetReport;
    onDrillDown: GlTableCallbacks["onDrillDown"];
}

/**
 * Balance Sheet table rendered via the shared ViewTable runtime.
 * - Columns are built dynamically from report.periods (ephemeral, never persisted)
 * - Total-period columns are pinned right via view.columns[i].options.pin
 * - All sections start expanded (autoExpandOnMount)
 */
export const BalanceSheetTable: React.FC<Props> = ({ report, onDrillDown }) => {
    const viewTableRef = useRef<ViewTableHandle>(null);

    const { numberFormat } = useContext(AccountingReportContext);

    const rows = useMemo(() => buildBalanceSheetRows(report), [report]);

    const view = useMemo(
        () => buildBalanceSheetView(report.periods),
        [report.periods],
    );

    const tableMeta: GlTableMeta = useMemo(
        () => ({ callbacks: { onDrillDown } }),
        [onDrillDown],
    );

    const config = useMemo(
        () => buildBalanceSheetConfig(report.periods, numberFormat),
        [report.periods, numberFormat],
    );

    const isByClassReport = useMemo(
        () => isNLevelClassReport(report.periods),
        [report.periods],
    );

    // Re-mount ViewTable (and reset autoExpandOnMount) when entity or date range changes.
    const reportKey = useMemo(
        () => report.periods.map(getPeriodKey).join("|"),
        [report.periods],
    );

    // Scroll to the rightmost column so the most recent period is visible.
    useEffect(() => {
        const timerId = window.setTimeout(() => {
            viewTableRef.current?.scrollTo({ left: Number.MAX_SAFE_INTEGER });
        }, 0);
        return () => window.clearTimeout(timerId);
    }, [report]);

    return (
        <ViewTable
            key={reportKey}
            ref={viewTableRef}
            data={rows}
            view={view}
            config={config}
            tableMeta={tableMeta}
            className={classNames(styles.balanceSheetTable, {
                [styles.byClassReport]: isByClassReport,
            })}
            centerColumns={hasSingleValueColumn(report.periods)}
            rowBorders={false}
            enableColumnResizing
            autoExpandOnMount
            nestingIndentPx={16}
            fixedTopOffsetPx
        />
    );
};
